import { useEffect, useMemo, useRef } from "react";
import { FormattedUpload, UploadStatus } from "./types";

const IN_FLIGHT: UploadStatus[] = ["parsing", "processing", "reversing"];
const FAST_MS = 5000;
const SLOW_MS = 60000;
const FRESH_MS = 2 * 60 * 1000;

export function useUploadPolling(
  records: FormattedUpload[],
  reload: () => void,
): void {
  const firstSeenInFlight = useRef<Record<string, number>>({});

  const freshUntil = useMemo(() => {
    const now = Date.now();
    const seen: Record<string, number> = {};
    records
      .filter((record) => IN_FLIGHT.includes(record.status))
      .forEach((record) => {
        const key = `${record.id}:${record.status}`;
        seen[key] = firstSeenInFlight.current[key] ?? now;
      });
    firstSeenInFlight.current = seen;

    const startedAt = Object.values(seen);
    return startedAt.length === 0 ? null : Math.max(...startedAt) + FRESH_MS;
  }, [records]);

  useEffect(() => {
    if (freshUntil === null) return undefined;

    const freshFor = freshUntil - Date.now();
    let id = setInterval(reload, freshFor > 0 ? FAST_MS : SLOW_MS);
    const slowDown = setTimeout(() => {
      clearInterval(id);
      id = setInterval(reload, SLOW_MS);
    }, Math.max(freshFor, 0));

    return () => {
      clearInterval(id);
      clearTimeout(slowDown);
    };
  }, [freshUntil, reload]);

  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === "visible") reload();
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [reload]);
}
